import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {
    confirmOwnPasswordReset,
    requestOwnPasswordReset,
    passwordResetErrorMessage
} from '../../../services/selfProfilePasswordReset';
import { t } from './translations';
import CodeInput from './CodeInput';

export default function ResetPasswordDialog({ user, onClose, onSuccess }) {
    const [delivery, setDelivery] = useState(null);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState(null);
    const [remaining, setRemaining] = useState(0);
    const [visible, setVisible] = useState({});
    const active = useRef(true);
    const lock = useRef(false);
    const resendAfter = useRef(0);
    useEffect(() => {
        active.current = true;
        return () => {
            active.current = false;
        };
    }, []);
    useEffect(() => {
        if (!delivery) return undefined;
        const interval = setInterval(
            () =>
                setRemaining(
                    Math.max(0, Math.ceil((resendAfter.current - Date.now()) / 1000))
                ),
            1000
        );
        return () => clearInterval(interval);
    }, [delivery]);
    const formik = useFormik({
        initialValues: { code: '', newPassword: '', confirmPassword: '' },
        validationSchema: Yup.object({
            code: Yup.string()
                .trim()
                .required('Introduza o código recebido.')
                .matches(/^\d{6}$/, 'Introduza os 6 dígitos do código.'),
            newPassword: Yup.string()
                .required('Introduza a nova palavra-passe.')
                .max(256, 'Utilize até 256 caracteres.'),
            confirmPassword: Yup.string()
                .required('Confirme a nova palavra-passe.')
                .oneOf([Yup.ref('newPassword')], 'As palavras-passe devem coincidir.')
        }),
        onSubmit: async ({ code, newPassword }, { resetForm }) => {
            if (lock.current || !delivery) return;
            lock.current = true;
            setNotice(null);
            try {
                await confirmOwnPasswordReset(user, code, newPassword);
                if (active.current) {
                    resetForm();
                    setVisible({});
                    onSuccess();
                }
            } catch (error) {
                if (active.current)
                    setNotice({
                        severity: 'error',
                        text: passwordResetErrorMessage(error)
                    });
            } finally {
                lock.current = false;
            }
        }
    });
    const busy = sending || formik.isSubmitting;
    const send = async () => {
        if (lock.current || Date.now() < resendAfter.current) return;
        lock.current = true;
        setSending(true);
        setNotice(null);
        try {
            const nextDelivery = await requestOwnPasswordReset(user);
            if (active.current) {
                setDelivery(nextDelivery);
                resendAfter.current = Date.now() + 30000;
                setRemaining(30);
                formik.setFieldValue('code', '', false);
                formik.setFieldTouched('code', false, false);
                if (delivery)
                    setNotice({
                        severity: 'success',
                        text: 'Enviámos um novo código de recuperação.'
                    });
            }
        } catch (error) {
            if (active.current)
                setNotice({ severity: 'error', text: passwordResetErrorMessage(error) });
        } finally {
            lock.current = false;
            if (active.current) setSending(false);
        }
    };
    const field = (name, label) => (
        <TextField
            name={name}
            label={t(label)}
            fullWidth
            required
            type={visible[name] ? 'text' : 'password'}
            autoComplete="new-password"
            value={formik.values[name]}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched[name] && formik.errors[name])}
            helperText={formik.touched[name] && t(formik.errors[name])}
            disabled={busy}
            inputProps={{ maxLength: 256 }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            type="button"
                            disabled={busy}
                            aria-label={t(
                                visible[name]
                                    ? 'Ocultar palavra-passe'
                                    : 'Mostrar palavra-passe'
                            )}
                            aria-pressed={Boolean(visible[name])}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() =>
                                setVisible((previous) => ({
                                    ...previous,
                                    [name]: !previous[name]
                                }))
                            }
                        >
                            {visible[name] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            aria-labelledby="reset-own-password-title"
            onClose={() => {
                if (!busy) onClose();
            }}
        >
            <Box component="form" noValidate onSubmit={formik.handleSubmit}>
                <DialogTitle id="reset-own-password-title">
                    {t('Redefinir palavra-passe')}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            label={t('Email da conta')}
                            value={user.email || ''}
                            InputProps={{ readOnly: true }}
                        />
                        {!delivery ? (
                            <Typography color="text.secondary">
                                {t(
                                    'Enviaremos um código de recuperação para confirmar a sua identidade. Não precisa da palavra-passe atual.'
                                )}
                            </Typography>
                        ) : (
                            <>
                                <Typography color="text.secondary" role="status">
                                    {t(
                                        delivery.medium === 'SMS'
                                            ? 'Código de recuperação enviado por SMS.'
                                            : 'Código de recuperação enviado.'
                                    )}
                                    {delivery.destination && <> {delivery.destination}</>}
                                </Typography>
                                <CodeInput
                                    id="password-reset-code"
                                    name="code"
                                    length={6}
                                    label={t('Código de recuperação')}
                                    disabled={busy}
                                    value={formik.values.code}
                                    onChange={(value) =>
                                        formik.setFieldValue('code', value)
                                    }
                                    onBlur={formik.handleBlur}
                                    error={Boolean(
                                        (formik.touched.code || formik.submitCount > 0) &&
                                            formik.errors.code
                                    )}
                                    helperText={t(
                                        (formik.touched.code || formik.submitCount > 0) &&
                                            formik.errors.code
                                            ? formik.errors.code
                                            : 'Cole ou escreva os 6 dígitos do código.'
                                    )}
                                />
                                {field('newPassword', 'Nova palavra-passe')}
                                {field('confirmPassword', 'Confirmar nova palavra-passe')}
                                <Box>
                                    <Button
                                        type="button"
                                        disabled={busy || remaining > 0}
                                        onClick={send}
                                    >
                                        {t('Reenviar código')}
                                        {remaining > 0 ? ' (' + remaining + 's)' : ''}
                                    </Button>
                                </Box>
                            </>
                        )}
                        {notice && (
                            <Alert severity={notice.severity}>{t(notice.text)}</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button type="button" disabled={busy} onClick={onClose}>
                        {t('Cancelar')}
                    </Button>
                    {delivery ? (
                        <Button type="submit" variant="contained" disabled={busy}>
                            {t(busy ? 'A guardar…' : 'Guardar nova palavra-passe')}
                        </Button>
                    ) : (
                        <Button
                            type="button"
                            variant="contained"
                            disabled={busy || !user.email}
                            onClick={send}
                            startIcon={
                                sending ? (
                                    <CircularProgress color="inherit" size={18} />
                                ) : undefined
                            }
                        >
                            {t(sending ? 'A enviar…' : 'Enviar código')}
                        </Button>
                    )}
                </DialogActions>
            </Box>
        </Dialog>
    );
}
ResetPasswordDialog.propTypes = {
    user: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired
};
